import React from "react";
import { Link } from "react-router-dom";
import ResponsiveImage, { ImageVariants } from "@/components/ResponsiveImage";

type KujiCardProps = {
  id: number;
  uid: string;
  siteId: string;
  siteSlug?: string;
  name: string;
  description?: string;
  imageUrl: string;
  imageVariants?: ImageVariants;
  status?: "draft" | "ready" | "published" | "active" | "finished" | "cancelled" | "archived";
  status_label?: string;
  starts_at?: string;
  ends_at?: string;
  soldOut?: boolean;
};

const KujiCard: React.FC<KujiCardProps> = ({
  uid,
  name,
  imageUrl,
  imageVariants,
  status,
  status_label,
  starts_at,
  ends_at,
  soldOut,
}) => {
  // 開催期間の判定
  const now = new Date();
  const startsAtDate = starts_at ? new Date(starts_at) : null;
  const endsAtDate = ends_at ? new Date(ends_at) : null;

  type PeriodStatus = 'before' | 'during' | 'after' | 'no_period';
  const getPeriodStatus = (): PeriodStatus => {
    if (!startsAtDate && !endsAtDate) return 'no_period';
    if (startsAtDate && now < startsAtDate) return 'before';
    if (endsAtDate && now > endsAtDate) return 'after';
    return 'during';
  };
  const periodStatus = getPeriodStatus();
  const isBeforePeriod = periodStatus === 'before';
  const isAfterPeriod = periodStatus === 'after';

  // イベント状態判定
  const isEventFinished = status === 'finished' || status === 'cancelled' || isAfterPeriod;
  const isEventReady = status === 'ready' || isBeforePeriod;
  const isEventActive = (status === 'active' || status === 'published') && !isBeforePeriod && !isAfterPeriod;
  const isSoldOut = soldOut && isEventActive;

  // NEW 判定（開催1週間以内）
  const isNew = (): boolean => {
    if (!starts_at || !isEventActive) return false;
    const startDate = new Date(starts_at);
    const now = new Date();
    const diffDays = (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 7;
  };

  // 終了日時のフォーマット
  const formatEndDate = (endsAt: string | undefined): string | null => {
    if (!endsAt) return null;
    const date = new Date(endsAt);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const weekday = weekdays[date.getDay()];
    return `${year}年${month}月${day}日 (${weekday}) ${hours}:${minutes}`;
  };

  // リンク先（くじは /kuji/ パス）
  const eventLink = `/kuji/${uid}`;

  // ステータスバッジの内容を決定
  const getStatusBadge = () => {
    // 期間による判定を優先
    if (isBeforePeriod) {
      return { label: '開催前', className: 'bg-blue-400 text-black' };
    }
    if (isAfterPeriod) {
      return { label: '終了', className: 'bg-gray-400 text-white' };
    }
    // ステータスによる判定
    if (status === 'ready') {
      return { label: '販売予定', className: 'bg-blue-400 text-black' };
    }
    if (status === 'finished' || status === 'cancelled') {
      return { label: '販売終了', className: 'bg-gray-400 text-white' };
    }
    if (isSoldOut) {
      return { label: '完売', className: 'bg-gray-400 text-white' };
    }
    if (isNew()) {
      return { label: 'NEW', className: 'bg-red-500 text-white' };
    }
    if (isEventActive) {
      return { label: '販売中', className: 'bg-green-500 text-white' };
    }
    return null;
  };

  const statusBadge = getStatusBadge();

  return (
    <Link to={eventLink} className="group relative block">
      <div className="bg-white border-4 border-black rounded-xl overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all">
        {/* メイン画像 */}
        <div className="relative aspect-[800/525] bg-gray-100">
          <ResponsiveImage
            src={imageUrl}
            variants={imageVariants}
            alt={name}
            displaySize="card"
            className={`w-full h-full object-contain transition duration-500 ${
              isEventFinished || isEventReady || isSoldOut
                ? 'grayscale opacity-60'
                : ''
            }`}
          />

          {(isEventFinished || isEventReady || isSoldOut) && (
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <div className="bg-white border-4 border-black px-6 py-3 rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <span className="text-lg font-black text-black">
                  {isBeforePeriod ? '開催前' : isAfterPeriod ? '終了' : status === 'ready' ? '販売予定' : isSoldOut ? '完売' : '販売終了'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* コンテンツエリア */}
        <div className="border-t-4 border-black bg-white px-4 py-3">
          {/* ステータスバッジ */}
          {statusBadge && (
            <div className="mb-2">
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            </div>
          )}
          <h3 className="font-bold text-base text-gray-900 line-clamp-2">{name}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {ends_at ? formatEndDate(ends_at) : '開催期間未定'}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default KujiCard;
